import React, { useState } from 'react';
import { LoadingSpinner } from '../Loading/LoadingSpinner';

interface AdminLoginProps {
  onLogin: (credentials: { username: string; password: string }) => Promise<void>;
  loading?: boolean;
  error?: string;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin, loading = false, error }) => {
  const [credentials, setCredentials] = useState({
    username: '', // This will actually be email
    password: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) return;
    
    // Now username field contains email
    await onLogin(credentials);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--binance-dark)' }}>
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `linear-gradient(var(--binance-border) 1px, transparent 1px),
                         linear-gradient(90deg, var(--binance-border) 1px, transparent 1px)`,
        backgroundSize: '20px 20px'
      }}></div>

      <div className="max-w-md w-full relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          {/* Binance-style logo */}
          <div className="mx-auto w-16 h-16 rounded-lg flex items-center justify-center mb-6 binance-card">
            <svg className="w-8 h-8" style={{ color: 'var(--binance-yellow)' }} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--binance-text-primary)' }}>
            PGC Admin Panel
          </h1>
          <p className="text-sm" style={{ color: 'var(--binance-text-secondary)' }}>
            Sign in to your account
          </p>
        </div>

        {/* Login Form */}
        <div className="binance-card p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium mb-2" style={{ color: 'var(--binance-text-primary)' }}>
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4" style={{ color: 'var(--binance-text-secondary)' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  id="username"
                  name="username"
                  type="email"
                  required
                  autoComplete="email"
                  value={credentials.username}
                  onChange={handleChange}
                  className="binance-input w-full pl-10 pr-3 py-3 text-sm"
                  placeholder="Enter your email address"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2" style={{ color: 'var(--binance-text-primary)' }}>
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4" style={{ color: 'var(--binance-text-secondary)' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={credentials.password}
                  onChange={handleChange}
                  className="binance-input w-full pl-10 pr-3 py-3 text-sm"
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 rounded border" style={{ 
                backgroundColor: 'rgba(248, 73, 96, 0.1)', 
                borderColor: 'var(--binance-danger)',
                color: 'var(--binance-danger)'
              }}>
                <div className="flex items-center text-sm">
                  <svg className="h-4 w-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{error}</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !credentials.username || !credentials.password}
              className="binance-button w-full py-3 px-4 text-sm font-semibold flex justify-center items-center"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin mr-2" 
                       style={{ borderColor: 'var(--binance-dark)', borderTopColor: 'transparent' }}></div>
                  <span className="ml-2">Signing in...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 102 0V4a1 1 0 011-1h10a1 1 0 011 1v12a1 1 0 102 0V4a1 1 0 00-1-1H4a1 1 0 00-1 1v12a1 1 0 102 0V4z" clipRule="evenodd" />
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H7a1 1 0 110-2h7.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Sign In
                </>
              )}
            </button>
          </form>


        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs" style={{ color: 'var(--binance-text-secondary)' }}>
            © 2024 PetGasCoin • Secure Admin Portal
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;